import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

export const TransactionBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open, close } = useCommands();
    const {
        selectedTransactionCount = 0,
        transactionBulkEntityOptions,
        transactionBulkCounterpartyOptions,
        transactionBulkCustomerOptions,
        transactionBulkCategoryOptions,
        transactionBulkCoaOptions,
        transactionBulkAccrualCoaOptions,
        transactionBulkScheduleOptions,
        transactionBulkClassTree,
        isTransactionBulkFieldLocked,
        transactionBulkFieldLockedTooltip,
        isTransactionBulkUnsplitLocked,
        transactionBulkUnsplitLockedTooltip,
        onTransactionBulkSetMemo,
        onTransactionBulkRemoveMemo,
        onTransactionBulkUnmatchAll,
        onTransactionBulkMatch,
        onTransactionBulkUnmatchInvoices,
        onTransactionBulkAcceptInvoiceMatches,
        onTransactionBulkAcceptBillMatches,
        onTransactionBulkUnsplitAll,
        onTransactionBulkCopyLink,
        onTransactionBulkCreateTask,
        onTransactionBulkRevertGL,
        onTransactionBulkRemoveCounterparty,
        onTransactionBulkRemoveCustomer,
        onTransactionBulkInvoiceToCustomer,
        onTransactionBulkRemoveClasses,
        onTransactionBulkDelete,
        onTransactionBulkReconcileGusto,
        onTransactionBulkMarkReviewedForFields,
        onTransactionBulkUnreviewForFields,
        onTransactionBulkExclude1099,
        onTransactionBulkInclude1099,
    } = options;

    const heading = `${pluralizeWithCount("transaction", selectedTransactionCount)} selected`;

    return (
        <Command.Group heading={heading}>
            {/* Mark reviewed */}
            {onTransactionBulkMarkReviewedForFields && (
                <CommandItem
                    keywords={["review", "mark reviewed", "approve"]}
                    onSelect={() =>
                        open(
                            CommandsDirectory.TRANSACTION_BULK_MARK_REVIEWED,
                            options,
                        )
                    }
                >
                    Mark reviewed
                </CommandItem>
            )}
            {/* Mark unreviewed — conditional on selected transactions being reviewed */}
            {onTransactionBulkUnreviewForFields && (
                <CommandItem
                    keywords={["unreview", "unmark", "remove review"]}
                    onSelect={() =>
                        open(
                            CommandsDirectory.TRANSACTION_BULK_UNREVIEW,
                            options,
                        )
                    }
                >
                    Mark unreviewed
                </CommandItem>
            )}

            {/* Create task */}
            {onTransactionBulkCreateTask && (
                <CommandItem
                    keywords={["task", "create"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkCreateTask();
                    }}
                    shortcut="t"
                >
                    Create task
                </CommandItem>
            )}

            {/* Set category */}
            {transactionBulkCategoryOptions &&
                transactionBulkCategoryOptions.length > 0 && (
                    <CommandItem
                        keywords={["category", "expense", "income"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_CATEGORY,
                                options,
                            )
                        }
                        shortcut="c"
                    >
                        Set category
                    </CommandItem>
                )}

            {/* Set counterparty */}
            {transactionBulkCounterpartyOptions &&
                transactionBulkCounterpartyOptions.length > 0 && (
                    <CommandItem
                        keywords={["counterparty", "vendor", "payee"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_COUNTERPARTY,
                                options,
                            )
                        }
                        shortcut="v"
                    >
                        Set counterparty
                    </CommandItem>
                )}
            {/* Remove counterparty — conditional on selected transactions having a counterparty */}
            {onTransactionBulkRemoveCounterparty && (
                <CommandItem
                    keywords={["remove", "counterparty"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkRemoveCounterparty();
                    }}
                >
                    Remove counterparty
                </CommandItem>
            )}

            {/* Set billable customer */}
            {transactionBulkCustomerOptions &&
                transactionBulkCustomerOptions.length > 0 && (
                    <CommandItem
                        keywords={["customer", "billable", "expense"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_CUSTOMER,
                                options,
                            )
                        }
                    >
                        Set billable customer
                    </CommandItem>
                )}
            {/* Remove billable customer — conditional on selected transactions having a customer */}
            {onTransactionBulkRemoveCustomer && (
                <CommandItem
                    keywords={["remove", "customer"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkRemoveCustomer();
                    }}
                >
                    Remove billable customer
                </CommandItem>
            )}
            {onTransactionBulkInvoiceToCustomer && (
                <CommandItem
                    keywords={["invoice", "customer", "billable", "expense"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkInvoiceToCustomer();
                    }}
                >
                    Invoice to customer
                </CommandItem>
            )}

            {/* Remove classes — conditional on selected transactions having classes */}
            {onTransactionBulkRemoveClasses && (
                <CommandItem
                    keywords={["remove", "classes", "class"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        void onTransactionBulkRemoveClasses();
                    }}
                >
                    Remove classes
                </CommandItem>
            )}

            {/* Set class */}
            {transactionBulkClassTree &&
                transactionBulkClassTree.length > 0 && (
                    <CommandItem
                        keywords={["class", "tag"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_CLASS,
                                options,
                            )
                        }
                        shortcut="l"
                    >
                        Set class
                    </CommandItem>
                )}

            {/* Set cash ledger */}
            {transactionBulkCoaOptions &&
                transactionBulkCoaOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "cash ledger",
                            "general ledger",
                            "account",
                            "coa",
                        ]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_COA,
                                options,
                            )
                        }
                        shortcut="g"
                    >
                        Set cash ledger
                    </CommandItem>
                )}
            {/* Revert cash ledger — conditional on selected transactions having a manually set cash ledger */}
            {onTransactionBulkRevertGL && (
                <CommandItem
                    keywords={["revert", "cash ledger", "general ledger"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkRevertGL();
                    }}
                    shortcut="r"
                >
                    Revert cash ledger
                </CommandItem>
            )}

            {/* Set accrual ledger */}
            {transactionBulkAccrualCoaOptions &&
                transactionBulkAccrualCoaOptions.length > 0 && (
                    <CommandItem
                        keywords={["accrual", "ledger"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_ACCRUAL_COA,
                                options,
                            )
                        }
                        shortcut="a"
                    >
                        Set accrual ledger
                    </CommandItem>
                )}

            {/* Set schedule */}
            {transactionBulkScheduleOptions &&
                transactionBulkScheduleOptions.length > 0 && (
                    <CommandItem
                        keywords={["schedule", "deferred", "recognition"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_SCHEDULE,
                                options,
                            )
                        }
                    >
                        Set recognition schedule
                    </CommandItem>
                )}

            {/* Set entity */}
            {transactionBulkEntityOptions &&
                transactionBulkEntityOptions.length > 0 && (
                    <CommandItem
                        keywords={["entity", "business", "personal"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_ENTITY,
                                options,
                            )
                        }
                        shortcut="e"
                    >
                        Set entity
                    </CommandItem>
                )}

            {/* Set memo */}
            {onTransactionBulkSetMemo && (
                <CommandItem
                    keywords={["note", "memo"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkSetMemo();
                    }}
                    shortcut="m"
                >
                    Set memo
                </CommandItem>
            )}

            {/* Clear memo — conditional on selected transactions having a memo */}
            {onTransactionBulkRemoveMemo && (
                <CommandItem
                    keywords={["remove", "memo", "note"]}
                    onSelect={() => {
                        close();
                        void onTransactionBulkRemoveMemo();
                    }}
                >
                    Clear memo
                </CommandItem>
            )}

            {onTransactionBulkUnmatchAll && (
                <CommandItem
                    keywords={["unmatch", "transfer"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkUnmatchAll();
                    }}
                    shortcut="u"
                >
                    Unmatch all
                </CommandItem>
            )}
            {onTransactionBulkMatch && (
                <CommandItem
                    keywords={["match", "transfer"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        void onTransactionBulkMatch();
                    }}
                >
                    Match
                </CommandItem>
            )}
            {onTransactionBulkUnmatchInvoices && (
                <CommandItem
                    keywords={["unmatch", "invoice", "detach", "remove"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        void onTransactionBulkUnmatchInvoices();
                    }}
                >
                    Unmatch invoices
                </CommandItem>
            )}
            {onTransactionBulkAcceptInvoiceMatches && (
                <CommandItem
                    keywords={[
                        "confirm",
                        "accept",
                        "invoice",
                        "match",
                        "suggested",
                    ]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        void onTransactionBulkAcceptInvoiceMatches();
                    }}
                >
                    Confirm invoice matches
                </CommandItem>
            )}
            {onTransactionBulkAcceptBillMatches && (
                <CommandItem
                    keywords={[
                        "confirm",
                        "accept",
                        "bill",
                        "match",
                        "suggested",
                    ]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        void onTransactionBulkAcceptBillMatches();
                    }}
                >
                    Confirm bill matches
                </CommandItem>
            )}
            {onTransactionBulkUnsplitAll && (
                <CommandItem
                    keywords={["unsplit"]}
                    disabled={isTransactionBulkUnsplitLocked}
                    disabledTooltip={transactionBulkUnsplitLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkUnsplitAll();
                    }}
                    shortcut="s"
                >
                    Unsplit all
                </CommandItem>
            )}

            {/* Reconcile Gusto */}
            {onTransactionBulkReconcileGusto && (
                <CommandItem
                    keywords={["gusto", "reconcile", "payroll"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkReconcileGusto();
                    }}
                >
                    Reconcile Gusto
                </CommandItem>
            )}

            {/* Copy URL */}
            {onTransactionBulkCopyLink && (
                <CommandItem
                    keywords={["copy", "link", "url", "share"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkCopyLink();
                    }}
                >
                    Copy URL
                </CommandItem>
            )}

            {/* Exclude from 1099 — shown when at least one selected transaction is not excluded */}
            {onTransactionBulkExclude1099 && (
                <CommandItem
                    keywords={["exclude", "1099", "exclude from 1099"]}
                    onSelect={() => {
                        close();
                        void onTransactionBulkExclude1099();
                    }}
                >
                    Exclude from 1099
                </CommandItem>
            )}

            {/* Include in 1099 — shown when all selected transactions are excluded */}
            {onTransactionBulkInclude1099 && (
                <CommandItem
                    keywords={["include", "1099", "include in 1099"]}
                    onSelect={() => {
                        close();
                        void onTransactionBulkInclude1099();
                    }}
                >
                    Include in 1099
                </CommandItem>
            )}

            {onTransactionBulkDelete && (
                <CommandItem
                    keywords={["delete", "remove"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkDelete();
                    }}
                    shortcut="d"
                >
                    Delete
                </CommandItem>
            )}
        </Command.Group>
    );
};
